import React, { useState } from 'react';
import { View, StyleSheet, Pressable, ScrollView } from 'react-native';
import {
  Text,
  TextInput,
  useTheme,
  IconButton,
  Card,
  Menu,
  Button,
} from 'react-native-paper';
import { useFormContext, Controller } from 'react-hook-form';
import { Plus, X, Trash2, ChevronDown } from 'lucide-react-native';

// Weight unit options
const WEIGHT_UNITS = [
  { value: 'g', label: 'Grams (g)' },
  { value: 'kg', label: 'Kilograms (kg)' },
  { value: 'lb', label: 'Pounds (lb)' },
  { value: 'oz', label: 'Ounces (oz)' },
];

export function EquipmentSpecifications() {
  const theme = useTheme();
  const colors = theme.colors;
  const isDark = theme.dark;
  const { control, watch, setValue } = useFormContext();

  const specifications = watch('specifications') || {};
  const features = watch('features') || [];
  const materials = watch('materials') || [];
  const colorOptions = watch('color_options') || [];
  const dimensions = watch('dimensions') || { width: undefined, height: undefined, depth: undefined, unit: 'cm' };
  const weight = watch('weight');
  const weightUnit = watch('weight_unit') || 'g';

  const [newSpecKey, setNewSpecKey] = useState('');
  const [newSpecValue, setNewSpecValue] = useState('');
  const [newFeature, setNewFeature] = useState('');
  const [newMaterial, setNewMaterial] = useState('');
  const [newColor, setNewColor] = useState('');
  const [weightUnitMenuVisible, setWeightUnitMenuVisible] = useState(false);
  const [weightInputValue, setWeightInputValue] = useState('');

  const addSpecification = () => {
    if (newSpecKey.trim() && newSpecValue.trim()) {
      setValue('specifications', {
        ...specifications,
        [newSpecKey.trim()]: newSpecValue.trim(),
      });
      setNewSpecKey('');
      setNewSpecValue('');
    }
  };

  const removeSpecification = (key: string) => {
    const updated = { ...specifications };
    delete updated[key];
    setValue('specifications', updated);
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      setValue('features', [...features, newFeature.trim()]);
      setNewFeature('');
    }
  };

  const removeFeature = (index: number) => {
    setValue(
      'features',
      features.filter((_: any, i: number) => i !== index)
    );
  };

  const addMaterial = () => {
    if (newMaterial.trim()) {
      setValue('materials', [...materials, newMaterial.trim()]);
      setNewMaterial('');
    }
  };

  const removeMaterial = (index: number) => {
    setValue(
      'materials',
      materials.filter((_: any, i: number) => i !== index)
    );
  };

  const addColor = () => {
    if (newColor.trim()) {
      setValue('color_options', [...colorOptions, newColor.trim()]);
      setNewColor('');
    }
  };

  const removeColor = (index: number) => {
    setValue(
      'color_options',
      colorOptions.filter((_: any, i: number) => i !== index)
    );
  };

  return (
    <View style={styles.container}>
      {/* Dimensions */}
      <View style={styles.fieldContainer}>
        <Text
          variant='labelLarge'
          style={[styles.label, { color: colors.onSurface }]}
        >
          Dimensions
        </Text>
        <Text
          variant='bodySmall'
          style={[styles.helperText, { color: colors.onSurfaceVariant }]}
        >
          Enter dimensions in centimeters
        </Text>

        <View style={styles.dimensionsRow}>
          <View style={styles.dimensionInput}>
            <Text
              variant='labelSmall'
              style={{ color: colors.onSurfaceVariant, marginBottom: 6 }}
            >
              Width (cm)
            </Text>
            <TextInput
              mode='outlined'
              placeholder='e.g., 15.5'
              value={dimensions.width !== undefined && dimensions.width !== null ? dimensions.width.toString() : ''}
              onChangeText={text => {
                if (text === '' || text.trim() === '') {
                  setValue('dimensions', { ...dimensions, width: undefined });
                } else {
                  const num = parseFloat(text);
                  if (!isNaN(num)) {
                    setValue('dimensions', { ...dimensions, width: num });
                  }
                }
              }}
              keyboardType='decimal-pad'
              style={styles.input}
              outlineStyle={styles.inputOutline}
              dense
            />
          </View>

          <View style={styles.dimensionInput}>
            <Text
              variant='labelSmall'
              style={{ color: colors.onSurfaceVariant, marginBottom: 6 }}
            >
              Height (cm)
            </Text>
            <TextInput
              mode='outlined'
              placeholder='e.g., 30.0'
              value={dimensions.height !== undefined && dimensions.height !== null ? dimensions.height.toString() : ''}
              onChangeText={text => {
                if (text === '' || text.trim() === '') {
                  setValue('dimensions', { ...dimensions, height: undefined });
                } else {
                  const num = parseFloat(text);
                  if (!isNaN(num)) {
                    setValue('dimensions', { ...dimensions, height: num });
                  }
                }
              }}
              keyboardType='decimal-pad'
              style={styles.input}
              outlineStyle={styles.inputOutline}
              dense
            />
          </View>

          <View style={styles.dimensionInput}>
            <Text
              variant='labelSmall'
              style={{ color: colors.onSurfaceVariant, marginBottom: 6 }}
            >
              Depth (cm)
            </Text>
            <TextInput
              mode='outlined'
              placeholder='e.g., 20.5'
              value={dimensions.depth !== undefined && dimensions.depth !== null ? dimensions.depth.toString() : ''}
              onChangeText={text => {
                if (text === '' || text.trim() === '') {
                  setValue('dimensions', { ...dimensions, depth: undefined });
                } else {
                  const num = parseFloat(text);
                  if (!isNaN(num)) {
                    setValue('dimensions', { ...dimensions, depth: num });
                  }
                }
              }}
              keyboardType='decimal-pad'
              style={styles.input}
              outlineStyle={styles.inputOutline}
              dense
            />
          </View>
        </View>
      </View>

      {/* Weight */}
      <View style={styles.fieldContainer}>
        <Text
          variant='labelLarge'
          style={[styles.label, { color: colors.onSurface }]}
        >
          Weight
        </Text>
        <Text
          variant='bodySmall'
          style={[styles.helperText, { color: colors.onSurfaceVariant }]}
        >
          Enter weight with unit
        </Text>
        <View style={styles.weightRow}>
          <View style={{ flex: 2 }}>
            <TextInput
              mode='outlined'
              placeholder='e.g., 1250.5'
              value={weightInputValue || (weight !== undefined && weight !== null ? weight.toString() : '')}
              onChangeText={text => {
                // Allow empty, numbers, and decimal point
                if (text === '' || text.trim() === '') {
                  setWeightInputValue('');
                  setValue('weight', undefined);
                } else if (/^\d*\.?\d*$/.test(text)) {
                  // Only allow valid decimal number format
                  setWeightInputValue(text);
                  
                  // Only update form value if it's a complete number
                  if (text !== '.' && text !== '' && !text.endsWith('.')) {
                    const num = parseFloat(text);
                    if (!isNaN(num)) {
                      setValue('weight', num);
                    }
                  }
                }
              }}
              onBlur={() => {
                // On blur, finalize the value
                if (weightInputValue) {
                  const num = parseFloat(weightInputValue);
                  if (!isNaN(num)) {
                    setValue('weight', num);
                    setWeightInputValue('');
                  } else {
                    setWeightInputValue('');
                    setValue('weight', undefined);
                  }
                }
              }}
              keyboardType='decimal-pad'
              style={styles.input}
              outlineStyle={styles.inputOutline}
            />
          </View>
          <View style={{ flex: 1 }}>
            <Menu
              visible={weightUnitMenuVisible}
              onDismiss={() => setWeightUnitMenuVisible(false)}
              anchor={
                <Pressable
                  onPress={() => setWeightUnitMenuVisible(true)}
                  style={[
                    styles.unitSelector,
                    {
                      backgroundColor: isDark
                        ? 'rgba(255, 255, 255, 0.05)'
                        : 'rgba(0, 0, 0, 0.02)',
                      borderColor: colors.outline,
                    },
                  ]}
                >
                  <Text
                    variant='bodyMedium'
                    style={[
                      styles.unitSelectorText,
                      { color: colors.onSurface },
                    ]}
                  >
                    {weightUnit}
                  </Text>
                  <ChevronDown
                    size={16}
                    color={colors.onSurfaceVariant}
                    strokeWidth={2}
                  />
                </Pressable>
              }
              contentStyle={[
                styles.menuContent,
                { backgroundColor: colors.surface },
              ]}
            >
              {WEIGHT_UNITS.map(unit => (
                <Menu.Item
                  key={unit.value}
                  onPress={() => {
                    setValue('weight_unit', unit.value);
                    setWeightUnitMenuVisible(false);
                  }}
                  title={unit.label}
                  titleStyle={{
                    color:
                      weightUnit === unit.value
                        ? colors.primary
                        : colors.onSurface,
                    fontWeight: weightUnit === unit.value ? '600' : '400',
                  }}
                  style={[
                    styles.menuItem,
                    weightUnit === unit.value && {
                      backgroundColor: isDark
                        ? 'rgba(103, 80, 164, 0.12)'
                        : 'rgba(103, 80, 164, 0.08)',
                    },
                  ]}
                />
              ))}
            </Menu>
          </View>
        </View>
      </View>

      {/* Custom Specifications */}
      <View style={styles.fieldContainer}>
        <Text
          variant='labelLarge'
          style={[styles.label, { color: colors.onSurface }]}
        >
          Technical Specifications
        </Text>
        <Text
          variant='bodySmall'
          style={[styles.helperText, { color: colors.onSurfaceVariant }]}
        >
          Add custom key-value specifications
        </Text>

        {/* Add New Spec */}
        <View style={styles.addSpecRow}>
          <View style={{ flex: 1 }}>
            <TextInput
              mode='outlined'
              placeholder='Key (e.g., Power)'
              value={newSpecKey}
              onChangeText={setNewSpecKey}
              style={styles.input}
              outlineStyle={styles.inputOutline}
              dense
            />
          </View>
          <View style={{ flex: 1 }}>
            <TextInput
              mode='outlined'
              placeholder='Value (e.g., 1200W)'
              value={newSpecValue}
              onChangeText={setNewSpecValue}
              style={styles.input}
              outlineStyle={styles.inputOutline}
              dense
            />
          </View>
          <IconButton
            icon={() => <Plus size={20} color={colors.primary} />}
            onPress={addSpecification}
            mode='contained'
            containerColor={colors.primaryContainer}
            size={20}
          />
        </View>

        {/* Spec List */}
        {Object.entries(specifications).map(([key, value]) => (
          <View
            key={key}
            style={[
              styles.specItem,
              {
                backgroundColor: isDark
                  ? 'rgba(255, 255, 255, 0.05)'
                  : 'rgba(0, 0, 0, 0.02)',
                borderColor: isDark
                  ? 'rgba(255, 255, 255, 0.1)'
                  : 'rgba(0, 0, 0, 0.08)',
              },
            ]}
          >
            <View style={{ flex: 1 }}>
              <Text variant='labelMedium' style={{ color: colors.onSurface }}>
                {key}
              </Text>
              <Text
                variant='bodySmall'
                style={{ color: colors.onSurfaceVariant }}
              >
                {value as string}
              </Text>
            </View>
            <IconButton
              icon={() => <Trash2 size={16} color={colors.error} />}
              onPress={() => removeSpecification(key)}
              size={16}
            />
          </View>
        ))}
      </View>

      {/* Features */}
      <View style={styles.fieldContainer}>
        <Text
          variant='labelLarge'
          style={[styles.label, { color: colors.onSurface }]}
        >
          Features
        </Text>
        <View style={styles.addRow}>
          <TextInput
            mode='outlined'
            placeholder='Add a feature...'
            value={newFeature}
            onChangeText={setNewFeature}
            style={[styles.input, { flex: 1 }]}
            outlineStyle={styles.inputOutline}
            onSubmitEditing={addFeature}
          />
          <IconButton
            icon={() => <Plus size={20} color={colors.primary} />}
            onPress={addFeature}
            mode='contained'
            containerColor={colors.primaryContainer}
            size={20}
          />
        </View>
        <View style={styles.chipContainer}>
          {features.map((feature: string, index: number) => (
            <View
              key={index}
              style={[
                styles.chip,
                {
                  backgroundColor: isDark
                    ? 'rgba(103, 80, 164, 0.15)'
                    : 'rgba(103, 80, 164, 0.08)',
                },
              ]}
            >
              <Text
                variant='bodySmall'
                style={{ color: colors.primary, flex: 1 }}
              >
                {feature}
              </Text>
              <Pressable onPress={() => removeFeature(index)}>
                <X size={14} color={colors.primary} />
              </Pressable>
            </View>
          ))}
        </View>
      </View>

      {/* Materials */}
      <View style={styles.fieldContainer}>
        <Text
          variant='labelLarge'
          style={[styles.label, { color: colors.onSurface }]}
        >
          Materials
        </Text>
        <View style={styles.addRow}>
          <TextInput
            mode='outlined'
            placeholder='Add a material...'
            value={newMaterial}
            onChangeText={setNewMaterial}
            style={[styles.input, { flex: 1 }]}
            outlineStyle={styles.inputOutline}
            onSubmitEditing={addMaterial}
          />
          <IconButton
            icon={() => <Plus size={20} color={colors.primary} />}
            onPress={addMaterial}
            mode='contained'
            containerColor={colors.primaryContainer}
            size={20}
          />
        </View>
        <View style={styles.chipContainer}>
          {materials.map((material: string, index: number) => (
            <View
              key={index}
              style={[
                styles.chip,
                {
                  backgroundColor: isDark
                    ? 'rgba(103, 80, 164, 0.15)'
                    : 'rgba(103, 80, 164, 0.08)',
                },
              ]}
            >
              <Text
                variant='bodySmall'
                style={{ color: colors.primary, flex: 1 }}
              >
                {material}
              </Text>
              <Pressable onPress={() => removeMaterial(index)}>
                <X size={14} color={colors.primary} />
              </Pressable>
            </View>
          ))}
        </View>
      </View>

      {/* Color Options */}
      <View style={styles.fieldContainer}>
        <Text
          variant='labelLarge'
          style={[styles.label, { color: colors.onSurface }]}
        >
          Color Options
        </Text>
        <View style={styles.addRow}>
          <TextInput
            mode='outlined'
            placeholder='Add a color option...'
            value={newColor}
            onChangeText={setNewColor}
            style={[styles.input, { flex: 1 }]}
            outlineStyle={styles.inputOutline}
            onSubmitEditing={addColor}
          />
          <IconButton
            icon={() => <Plus size={20} color={colors.primary} />}
            onPress={addColor}
            mode='contained'
            containerColor={colors.primaryContainer}
            size={20}
          />
        </View>
        <View style={styles.chipContainer}>
          {colorOptions.map((color: string, index: number) => (
            <View
              key={index}
              style={[
                styles.chip,
                {
                  backgroundColor: isDark
                    ? 'rgba(103, 80, 164, 0.15)'
                    : 'rgba(103, 80, 164, 0.08)',
                },
              ]}
            >
              <Text
                variant='bodySmall'
                style={{ color: colors.primary, flex: 1 }}
              >
                {color}
              </Text>
              <Pressable onPress={() => removeColor(index)}>
                <X size={14} color={colors.primary} />
              </Pressable>
            </View>
          ))}
        </View>
      </View>

      {/* Tips */}
      <Card
        style={[
          styles.card,
          styles.tipsCard,
          { backgroundColor: theme.colors.primaryContainer },
        ]}
      >
        <Card.Content>
          <Text
            variant='titleMedium'
            style={[
              styles.tipsTitle,
              { color: theme.colors.onPrimaryContainer },
            ]}
          >
            Tips for Specifications
          </Text>
          <Text
            variant='bodySmall'
            style={[
              styles.tipsText,
              { color: theme.colors.onPrimaryContainer },
            ]}
          >
            • Include accurate dimensions and weight{'\n'}• Add technical specs
            like power, capacity, or grind settings{'\n'}• List all materials
            used in construction{'\n'}• Mention available color options{'\n'}•
            Highlight key features that set this equipment apart
          </Text>
        </Card.Content>
      </Card>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 24,
  },
  fieldContainer: {
    gap: 8,
  },
  label: {
    fontWeight: '600',
  },
  helperText: {
    fontSize: 12,
    marginTop: -4,
  },
  input: {
    fontSize: 15,
  },
  inputOutline: {
    borderRadius: 12,
  },
  dimensionsRow: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 8,
  },
  dimensionInput: {
    flex: 1,
  },
  weightRow: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 8,
  },
  unitSelector: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 14,
    paddingVertical: 16,
    borderRadius: 12,
    borderWidth: 1,
    minHeight: 56,
  },
  unitSelectorText: {
    fontWeight: '500',
  },
  menuContent: {
    borderRadius: 12,
    marginTop: 8,
  },
  menuItem: {
    minHeight: 48,
    borderRadius: 8,
    marginHorizontal: 4,
    marginVertical: 2,
  },
  addSpecRow: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 8,
  },
  specItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 12,
    borderWidth: 1,
    marginTop: 8,
  },
  addRow: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 8,
  },
  chipContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 8,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
  },
  card: {
    marginTop: 8,
  },
  tipsCard: {
    marginTop: 8,
  },
  tipsTitle: {
    marginBottom: 8,
    fontWeight: 'bold',
  },
  tipsText: {
    lineHeight: 20,
  },
});
